"use client";

import React, { useEffect, useMemo } from "react";
import GiftBanner from "./gift-banner";
import GiftForm from "./gift-form";
import CustomizeProduct from "./customize-product";
import Box from "@mui/material/Box";
import { Trans } from "@lingui/macro";
import AlertComponent from "@/components/core/alert";
import { useAlertStore } from "@/components/context/alert-store";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Typography } from "@mui/material";
import { magicConstants } from "@/utils/magic-constants";
import { getRequest } from "@/utils/request";
import { useGiftStore } from "@/components/context/gift-store";
import { useProductStore } from "@/components/context/product-store";
import { useShallow } from "zustand/react/shallow";
import { useRouter } from "next/navigation";
const defaultProduct = {
  id: "",
  name: "",
  offerz_product_tabs: [],
  offerz_similar_products: [],
  offerz_options: { buy_options: [] },
  original_image: "",
  price: 0,
  seo_url: 0,
  seo_urls: {},
  special: 0,
  subtitle: "",
  type: "",
};

type Props = Readonly<{
  gift: any;
  lang: string;
  presetProduct: string | null;
  presetQuantity: number | null;
  presetproperties: string | null;
}>;

export default function CreateOrEditGift({
  gift,
  lang,
  presetProduct,
  presetQuantity,
  presetproperties,
}: Props) {
  const queryClient = useQueryClient();
  const { message, setMessage } = useAlertStore();

  const router = useRouter();

  const {
    productId,
    customizeProduct,
    buttonClicked,
    setProductId,
    setProductOption,
    setHasPriceOption,
    setErrorMessage,
    setProduct,
    reset,
  } = useGiftStore();

  useEffect(() => {
    return () => setProductId("0");
  }, [setProductId]);

  const { setQty: setQuantity } = useProductStore(
    useShallow((state) => ({
      qty: state.qty,
      setQty: state.setQty,
    })),
  );
  const { data: productData, isFetching: isLoadingProduct } = useQuery({
    queryKey: ["product", productId],
    queryFn: () =>
      getRequest({
        path: { name: "products", id: `${productId}` },
        lang,
      }),
    enabled: !!productId && productId !== "0",
  });

  useEffect(() => {
    if (presetProduct) {
      setProductId(presetProduct);
      if (presetQuantity) setQuantity(presetQuantity);
      if (presetproperties) {
        if (Object.keys(JSON.parse(presetproperties)).length === 0) {
          router.push("/create-gift");
        }
        try {
          const requiredOptions = JSON.parse(presetproperties);
          if (typeof requiredOptions === "object") {
            const option = [
              {
                key: String(Object.keys(requiredOptions)[0]),
                value: requiredOptions[Object.keys(requiredOptions)[0]],
              },
            ];
            setProductOption(option);
          }
        } catch (err) {
          console.error("Invalid presetproperties JSON", err);
        }
      }
    }
  }, [presetProduct, presetQuantity, presetproperties]);

  useEffect(() => {
    if (gift) {
      setProductId(gift.product_id);
      setQuantity(gift.quantity);
      queryClient.invalidateQueries({ queryKey: ["product"] });
    }
  }, [gift, queryClient]);

  useEffect(() => {
    if (productData?.success) {
      reset();
      setErrorMessage("product", "");
      const foundPriceOption = productData.data.offerz_options?.buy_options?.find(
        (opt: { option_id: string; product_option_id: string }) =>
          opt.option_id === magicConstants.priceOptionID,
      ) || { option_id: "", product_option_id: "" };
      setHasPriceOption(foundPriceOption);
      setProduct(productData.data);
    } else {
      setProduct(defaultProduct);
    }
  }, [productData]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [gift, customizeProduct]);

  const hasEmptyOption = useMemo(() => {
    if (!presetproperties) return false;
    try {
      const propertiesObj =
        typeof presetproperties === "string" ? JSON.parse(presetproperties) : presetproperties;
      return Object.values(propertiesObj).some((value) => value === "");
    } catch (err) {
      console.error("Error parsing presetproperties:", err);
      return false;
    }
  }, [presetproperties]);

  const showCustomizeProduct = useMemo(() => {
    if (hasEmptyOption) {
      return (gift || presetProduct !== null || customizeProduct) && buttonClicked;
    }
    return gift || customizeProduct || presetProduct !== null;
  }, [hasEmptyOption, gift, presetProduct, customizeProduct, buttonClicked]);

  return (
    <div className="container">
      {message && <AlertComponent message={message} setMessage={setMessage} />}
      {showCustomizeProduct ? (
        <div className="mb-2.5 mt-[60px] flex flex-col gap-2 text-sm md:mt-3 md:flex-row">
          <CustomizeProduct lang={lang} gift={gift || false} presetProduct={presetProduct} />
        </div>
      ) : (
        <div className="mb-2.5 flex flex-col gap-5 text-sm xs:mt-8 md:mt-0 md:flex-row lg:mt-0 xl:mt-0">
          <Box
            sx={{
              display: "flex",
              pt: { xs: "0", xl: "20px" },
              gap: 3,
              flexDirection: { xs: "column", md: "column", xl: "row" },
              width: "100%",
              alignItems: { xs: "center", md: "flex-start" },
            }}
          >
            <Box
              sx={{
                display: { xs: "none", md: "flex", lg: "flex", xl: "none" },
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  pb: "10px",
                  color: "#ff4a33",
                  pt: "20px",
                }}
              >
                <Trans>NEW</Trans>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "30px", md: "35px" },
                  fontWeight: 600,
                  pb: "10px",
                  lineHeight: "1.2",
                  color: "#1f1f25",
                }}
              >
                <Trans>Design and give away vouchers yourself</Trans>
              </Typography>
              <Typography
                sx={{ fontSize: "17px", pb: "10px", lineHeight: "1.35", color: "#1f1f25" }}
              >
                <Trans>
                  Design your own voucher with your photo or video. Choose from hundreds of vouchers
                  or our Offerz voucher, this one always fits!
                </Trans>
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexDirection: { xs: "column", md: "row", lg: "row" },
                width: "100%",
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  position: { xs: "relative", md: "sticky" },
                  top: { xs: 0, md: 105 },
                  zIndex: 1,
                }}
              >
                <GiftBanner />
              </Box>
              <GiftForm isLoadingProduct={isLoadingProduct} lang={lang} />
            </Box>
          </Box>
        </div>
      )}
    </div>
  );
}
